import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { Loader2, Sparkles, Save } from 'lucide-react';
import api from '@/services/api';

/**
 * ChannelBehaviorEditor — lightweight "per-channel workflow" panel.
 *
 * Lets an operator configure a *different* AI workflow for each connected
 * social channel: greeting, response tone, lead-collection style, AI
 * auto-reply on/off, human-yield timeout. Backed by
 * PUT /chatbots/{chatbotId}/channels/{platform}/behavior.
 */
const DEFAULTS = {
  ai_auto_reply: true,
  yield_to_human: true,
  yield_timeout_minutes: 5,
  collect_leads: true,
  lead_collection_style: 'conversational',
  greeting_message: '',
};

export const ChannelBehaviorEditor = ({ chatbotId, platform, label }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/chatbots/${chatbotId}/channels/${platform}/behavior`);
        if (!cancelled) setSettings({ ...DEFAULTS, ...(r.data || {}) });
      } catch {
        // Fall back to defaults if endpoint fails; still allow editing.
        if (!cancelled) setSettings(DEFAULTS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chatbotId, platform]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/chatbots/${chatbotId}/channels/${platform}/behavior`, settings);
      toast.success(`${label || platform} workflow saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  return (
    <Accordion type="single" collapsible className="border border-slate-200 rounded-lg bg-slate-50/60">
      <AccordionItem value="workflow" className="border-none">
        <AccordionTrigger
          className="px-4 py-3 hover:no-underline text-slate-800"
          data-testid={`${platform}-workflow-toggle`}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="font-medium">AI Workflow for {label || platform}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading current workflow…
            </div>
          ) : (
            <>
              <div className="grid gap-3">
                <div className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 border border-slate-200">
                  <div>
                    <div className="text-sm font-medium text-slate-800">Auto-reply with AI</div>
                    <div className="text-xs text-slate-500">If off, incoming messages are logged only (moderator replies manually).</div>
                  </div>
                  <Switch
                    checked={settings.ai_auto_reply}
                    onCheckedChange={(v) => set({ ai_auto_reply: v })}
                    data-testid={`${platform}-workflow-auto-reply`}
                  />
                </div>

                <div className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 border border-slate-200">
                  <div>
                    <div className="text-sm font-medium text-slate-800">Yield to human</div>
                    <div className="text-xs text-slate-500">When your team replies, the AI pauses for the timeout below.</div>
                  </div>
                  <Switch
                    checked={settings.yield_to_human}
                    onCheckedChange={(v) => set({ yield_to_human: v })}
                    data-testid={`${platform}-workflow-yield`}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Yield timeout (minutes)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      value={settings.yield_timeout_minutes}
                      onChange={(e) => set({ yield_timeout_minutes: Math.max(1, Number(e.target.value) || 5) })}
                      className="mt-1 bg-white"
                      data-testid={`${platform}-workflow-yield-timeout`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Lead-collection style</Label>
                    <Select
                      value={settings.lead_collection_style}
                      onValueChange={(v) => set({ lead_collection_style: v })}
                    >
                      <SelectTrigger className="mt-1 bg-white" data-testid={`${platform}-workflow-lead-style`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conversational">Conversational (natural)</SelectItem>
                        <SelectItem value="direct">Direct (ask early)</SelectItem>
                        <SelectItem value="passive">Passive (only when volunteered)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 border border-slate-200">
                  <div>
                    <div className="text-sm font-medium text-slate-800">Collect leads</div>
                    <div className="text-xs text-slate-500">Politely capture name, email or phone when appropriate.</div>
                  </div>
                  <Switch
                    checked={settings.collect_leads}
                    onCheckedChange={(v) => set({ collect_leads: v })}
                    data-testid={`${platform}-workflow-collect-leads`}
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-600">Greeting (first message the AI sends on this channel)</Label>
                  <Textarea
                    rows={2}
                    placeholder={`Hi 👋 Thanks for reaching out on ${label || platform}! How can I help?`}
                    value={settings.greeting_message || ''}
                    onChange={(e) => set({ greeting_message: e.target.value })}
                    className="mt-1 bg-white resize-none"
                    data-testid={`${platform}-workflow-greeting`}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Leave blank to reuse the chatbot&apos;s default greeting.
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={save}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid={`${platform}-workflow-save`}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save workflow
                </Button>
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default ChannelBehaviorEditor;
